/**
 * Halloween tee designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenTeeDesignsPack: ToilePack = {
    slug: 'halloween-tee-designs',
    category: 'season',
    name: 'Halloween tee designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Slogan', subject: 'The words "SPOOKY SEASON" lettered in drippy retro type with a bat, isolated on a plain ground'},
        {key: 'ghost', label: 'Cartoon ghost', subject: 'A cartoon ghost holding a candy bucket, isolated on a plain ground, no text'},
        {key: 'badge', label: 'Retro badge', subject: 'A carved pumpkin in a retro sunburst circle with a distressed texture, isolated, no text'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['for a toddler shirt', 'as a back print', 'in glow-in-the-dark colors'],
    directionHint: 'try: for a toddler shirt instead',
    seo: {
        title: 'Halloween Tee Designs: Slogan, Ghost, Badge Art',
        metaDescription: 'Nine halloween tee designs -- slogan, cartoon ghost, retro badge -- in spooky cute, vintage, and bold graphic finishes. Isolated, no shirt.',
        h1: 'Halloween t-shirt design: slogan, cartoon ghost, and retro badge',
        tagline: 'Nine prints, three chest designs, three finishes each.',
        intro: "Nine print-ready halloween t-shirt designs: a 'Spooky Season' slogan with a bat, a cartoon ghost holding a candy bucket, and a retro pumpkin badge in a sunburst circle. Three finishes -- spooky cute, vintage, and bold graphic -- isolated with no shirt in frame. Ask for a toddler shirt instead, or glow-in-the-dark colors, and the same three finishes carry it.",
        useCases: [
            'a print-on-demand t-shirt',
            'a heat-transfer design',
            'a trick-or-treat costume shirt',
            'a matching group costume print',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine print designs -- a lettered slogan, a cartoon ghost, and a retro badge -- each in spooky cute, vintage, and bold graphic finishes, isolated with no shirt in frame.'},
            {q: 'Is this a mockup on an actual shirt?', a: 'No -- every design is isolated art on a plain ground, ready to place on whatever shirt or transfer paper you print to.'},
            {q: 'Can I get a toddler-size version?', a: "The design itself isn't pre-sized for a garment, but the direction field can request a toddler shirt instead as a simpler, smaller-scale composition."},
            {q: 'Can I print these in glow-in-the-dark ink?', a: 'Yes -- the flat art style works well with a glow-in-the-dark or single-color print run; ask for it via the direction field.'},
        ],
    },
}
